import { nanoid } from 'nanoid';

import type { Question } from '@api/instant_survey/types';
import { FOLDER_ID_CUSTOM, QUESTION_TYPES } from '@api/instant_survey';

/**
 * Strips ids for questions and answers when editing a recommended question or duplicating an existing question
 * @param {Object} options
 * @param {Object[]} options.availableQuestionTypes
 * @param {Boolean} [options.isDuplicate]
 * @param {Object} options.question
 * @param {Boolean} [options.shouldCreateFromOriginal]
 */
export const prepExistingQuestion = ({
  availableQuestionTypes,
  isDuplicate,
  question,
  shouldCreateFromOriginal,
}: {
  availableQuestionTypes: (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES][];
  isDuplicate?: boolean;
  question: Question;
  shouldCreateFromOriginal?: boolean;
}) => {
  const isCreating =
    shouldCreateFromOriginal ?? (isDuplicate || question.folder !== FOLDER_ID_CUSTOM);
  const answers = question.answers.map((answer) => ({
    ...answer,
    id: isCreating ? nanoid() : answer.id,
  }));
  const subQuestions = question.subQuestions.map((subQuestion) => ({
    ...subQuestion,
    id: isCreating ? nanoid() : subQuestion.id,
  }));

  return {
    ...question,
    answers: answers.filter(
      ({ isCarryForwardGenerated, isNoneOfTheAbove }) =>
        isCarryForwardGenerated || !isNoneOfTheAbove
    ),
    id: isCreating ? undefined : question.id,
    noneAnswer:
      answers.find(
        ({ isCarryForwardGenerated, isNoneOfTheAbove }) =>
          !isCarryForwardGenerated && isNoneOfTheAbove
      ) || null,
    properties: {
      ...question.properties,
    },
    subQuestions,
    type:
      availableQuestionTypes.find(({ value }) => value === question.type)?.value ||
      question.type ||
      QUESTION_TYPES.ESSAY.value,
  };
};

export type PipingQuestionOption = {
  display: string;
  id: string;
  type: string;
};

/**
 * Builds the list of eligible previous-answer piping sources shown in the
 * "Prev Answer" mention dropdown. Screener questions are excluded, and only
 * questions whose type supports piping are kept. Grid questions are flattened
 * into one entry per sub-question.
 * @param {Object} options
 * @param {String[]} options.canPipingResponseTypes - question types eligible as a piping source
 * @param {Object} options.pipingQuestionNumbersById - map of question id -> piping question number
 * @param {Object} options.questionDisplayIndexById - map of question id -> display index
 * @param {Object[]} options.questionsBeforeCurrent - questions positioned before the current one
 */
export const getSurveyQuestionsForPiping = ({
  canPipingResponseTypes,
  pipingQuestionNumbersById,
  questionDisplayIndexById,
  questionsBeforeCurrent,
}: {
  canPipingResponseTypes: string[];
  pipingQuestionNumbersById: Record<Question['id'], string>;
  questionDisplayIndexById: Record<Question['id'], string>;
  questionsBeforeCurrent: Question[];
}): PipingQuestionOption[] =>
  questionsBeforeCurrent
    .filter((question) => !question.isScreener)
    .flatMap((question) => {
      const questionDisplayIndex = questionDisplayIndexById[question.id];
      const pipingQuestionNumber = pipingQuestionNumbersById[question.id];
      return question.subQuestions?.length
        ? question.subQuestions.map((subQ, subIndex) => ({
            display: `Question ${questionDisplayIndex}.${subIndex + 1} - ${subQ.text}`,
            id: `answer from q${pipingQuestionNumber}.${subIndex + 1}`,
            mentionLabel: `answer from q${questionDisplayIndex}.${subIndex + 1}`,
            type: question.type,
          }))
        : {
            display: `Question ${questionDisplayIndex}: ${question.text}`,
            id: `answer from q${pipingQuestionNumber}`,
            mentionLabel: `answer from q${questionDisplayIndex}`,
            type: question.type,
          };
    })
    .filter(({ type }) => canPipingResponseTypes.includes(type));
